import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PoweredBy } from '../components/Brand';
import { loadErrorKey, markUrl, useMe, useProfile, useReceipt } from '../api';
import { dateTime, inr, monthLabel } from '../format';

/**
 * A receipt on one page, in English and Malayalam, for printing or saving as PDF from the browser
 * (pilot slice 7). The WhatsApp receipt arrives with the message outbox (slice 8).
 */
export function ReceiptPrint() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const me = useMe();
  const receipt = useReceipt(id);
  const r = receipt.data;
  const m = me.data?.mahallu;
  const profile = useProfile().data;
  const en = i18n.getFixedT('en');
  const ml = i18n.getFixedT('ml');
  const both = (key: Parameters<typeof en>[0]) => (
    <>
      {en(key)} <span className="ml note">· {ml(key)}</span>
    </>
  );
  if (receipt.isError) return <p className="callout">{t(loadErrorKey(receipt.error))}</p>;
  if (!r) return <p className="note">{t('drawer.loading')}</p>;
  return (
    <main className="public-page">
      <div className="action-row no-print" style={{ marginBottom: 12 }}>
        <span className="spacer" />
        <button type="button" className="btn primary sm" onClick={() => window.print()}>
          {t('collect.print')}
        </button>
      </div>
      <article className="receipt-sheet">
        {r.status === 'CANCELLED' ? <div className="receipt-void">{en('rcpt.CANCELLED').toUpperCase()}</div> : null}
        <header>
          {m && profile?.markHash ? <img src={markUrl(m.slug, profile.markHash)} alt="" style={{ maxHeight: 56, maxWidth: 160, marginBottom: 6 }} /> : null}
          <h1>{m?.nameEn}</h1>
          {m?.nameMl ? <div className="ml">{m.nameMl}</div> : null}
          {profile?.addressEn ? <p className="note">{[profile.addressEn, profile.district, profile.pinCode].filter(Boolean).join(', ')}</p> : null}
          {profile?.waqfRegNo ? <p className="note">{both('print.waqf')}: {profile.waqfRegNo}</p> : null}
          <p className="note">{both('print.receipt')}</p>
        </header>
        <div className="amount num">{inr(r.amountInr)}</div>
        <table>
          <tbody>
            <tr>
              <td>{both('rcpt.no')}</td>
              <td className="num">
                <b>{r.receiptNo}</b>
              </td>
            </tr>
            <tr>
              <td>{both('rcpt.date')}</td>
              <td className="num">{r.receivedOn}</td>
            </tr>
            <tr>
              <td>{both('rcpt.payer')}</td>
              <td>{r.house ? `${r.house.nameEn} (${r.house.houseNo})` : r.payerName}{r.house?.nameMl ? <span className="ml"> · {r.house.nameMl}</span> : null}</td>
            </tr>
            <tr>
              <td>{both('collect.fund')}</td>
              <td>
                {r.fund.nameEn}
                {r.fund.nameMl ? <span className="ml"> · {r.fund.nameMl}</span> : null}
              </td>
            </tr>
            {r.periods.length ? (
              <tr>
                <td>{both('print.months')}</td>
                <td>{r.periods.map((p) => monthLabel(p, 'en')).join(', ')}</td>
              </tr>
            ) : null}
            <tr>
              <td>{both('collect.method')}</td>
              <td>
                {en(`method.${r.method}`)}
                {r.reference ? ` · ${r.reference}` : ''}
              </td>
            </tr>
            <tr>
              <td>{both('rcpt.by')}</td>
              <td>
                {r.collectedBy} · {dateTime(r.createdAt, 'en')}
              </td>
            </tr>
            {r.status === 'CANCELLED' ? (
              <tr>
                <td>{both('rcpt.reason')}</td>
                <td>{r.cancelReason}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {r.fund.code !== 'VARISANKHYA' && (profile?.reg12a || profile?.reg80g) ? (
          <p className="note" style={{ marginTop: 12 }}>
            {[profile.reg12a ? `12A: ${profile.reg12a}` : null, profile.reg80g ? `80G: ${profile.reg80g}` : null].filter(Boolean).join(' · ')}
          </p>
        ) : null}
        <p className="note" style={{ marginTop: 18 }}>
          {both('print.footer')}
        </p>
        <PoweredBy />
      </article>
    </main>
  );
}
